"use client"
import { useCallback } from "react"
import { useContextSelector } from "use-context-selector"
import { SelectItemShortcutsContext } from "./SelectItemShortcutsProvider"

type Item = {
  id: string
}

type UseItemSelectionShortcutsProps = {
  items: Item[] | undefined
  selectedItems: Map<string, Item | undefined> | undefined
  selectItem: ((itemId: string, selectionState?: boolean) => void) | undefined
}

const elementAttributes = {
  draggable: false,
  style: {
    userSelect: "none",
  },
} as const satisfies React.HTMLAttributes<HTMLElement>

export function useSelectItemMouseShortcuts({
  items,
  selectItem,
  selectedItems,
}: UseItemSelectionShortcutsProps) {
  const lastSelectedIndexRef = useContextSelector(
    SelectItemShortcutsContext,
    v => v.lastSelectedIndexRef,
  )

  const handleOnClick = useCallback(
    (event: React.MouseEvent, itemIndex: number) => {
      event.preventDefault()
      event.stopPropagation()
      const item = items?.[itemIndex]

      if (!item) {
        return
      }

      if (event.shiftKey && typeof lastSelectedIndexRef?.current === "number") {
        if (
          // If we're selecting the same item as we last selected, select it as normal
          lastSelectedIndexRef.current === itemIndex ||
          // Or, if we're selecting the item next to the last selected item and they have differing selected states
          // Just select the item like normal
          (Math.abs(lastSelectedIndexRef.current - itemIndex) === 1 &&
            selectedItems?.has(items[lastSelectedIndexRef.current].id) !==
              selectedItems?.has(item.id))
        ) {
          selectItem?.(item.id)
        } else {
          const start = Math.min(lastSelectedIndexRef.current, itemIndex)
          const end = Math.max(lastSelectedIndexRef.current, itemIndex)
          const selectingInwards = itemIndex > lastSelectedIndexRef.current

          const itemsInRange = items.slice(start, end + 1)

          // If every item within the range is selected, then deselect all. Otherwise, select everything in range.
          // Based on if we're going inwards, we can strip off from the ends for this calculation.
          const everyItemSelected = itemsInRange
            .slice(
              0 + Number(selectingInwards),
              itemsInRange.length - Number(!selectingInwards),
            )
            .every(itemInRange => selectedItems?.has(itemInRange.id))

          itemsInRange.forEach(item => {
            selectItem?.(item.id, !everyItemSelected)
          })
        }
      } else {
        selectItem?.(item.id)
      }

      lastSelectedIndexRef.current = itemIndex
    },
    [items, lastSelectedIndexRef, selectedItems, selectItem],
  )

  return {
    handleOnClick,
    elementAttributes,
  }
}
